import React from 'react';
import { LandingPageOption, LandingPageType, Tabs } from './types';
import { FreemiumTemplateId } from 'lib/const/VideoTemplateItems';
import { MdLaptop, MdSmartphone } from 'react-icons/md';
import { TFunction } from 'i18next';
import { Trans } from 'react-i18next';
import { IWhiteLabel, WHITELABEL_NAME } from 'lib/api/whitelabel/getWhiteLabel';

// custom landing pages created by customers have this typeId
export const builderLandingPageTypeId = 19;
export const LANDING_PAGE_DESIGN_TYPE_ID = 21;

export const defaultLPOption: LandingPageOption = {
  value: 0,
  label: '',
  landingPageType: LandingPageType.VIDMAILS,
};

export const getFreemiumLPOption = (t: TFunction) => ({
  value: FreemiumTemplateId,
  label: t('Video Landing Page'),
  landingPageType: LandingPageType.VIDMAILS,
});

export const getFreemiumCTAOption = (t: TFunction) => ({
  value: '-1',
  label: t('None'),
});

// Legacy exports for backward compatibility
export const freemiumLPOption = {
  value: FreemiumTemplateId,
  label: 'Video Landing Page',
  landingPageType: LandingPageType.VIDMAILS,
};

export const freemiumCTAOption = {
  value: '-1',
  label: 'None',
};

export const localizedTabs = (t: TFunction): Tabs => ({
  send: {
    value: t('Send'),
    isHidden: false,
    isLocked: false,
    isLive: true,
    isDisabled: false,
  },
  share: {
    value: t('Share'),
    isHidden: false,
    isLocked: false,
    isLive: true,
    isDisabled: false,
  },
  embed: {
    value: t('Embed'),
    isHidden: false,
    isLocked: false,
    isLive: true,
    isDisabled: false,
  },
  publish: {
    value: t('Publish'),
    isHidden: false,
    isLocked: false,
    isLive: true,
    isDisabled: false,
  },
});

export const CREATE_NEW_ID = '0';

export const MAX_EMAIL_RECIPIENTS = 1000;
export const MAX_PHONE_RECIPIENTS = 1000;

export const getNoSmsMsg = (t: TFunction) => (
  <>
    {t('To activate the SMS functionality for your account, please call us at')}
    <br />
    <a href='tel:8003061445'>800-306-1445</a>
  </>
);

export const getNoInventoryItemsMsg = (t: TFunction) => (
  <>
    {t(
      'To enable the integration with your Inventory Management System, please call us at'
    )}
    <br />
    <a href='tel:8003061445'>800-306-1445</a>
  </>
);

// Legacy exports for backward compatibility
export const noSmsMsg = (
  <>
    To activate the SMS functionality for your account, please call us at
    <br />
    <a href='tel:8003061445'>800-306-1445</a>
  </>
);

export const noInventoryItemsMsg = (whitelabel: IWhiteLabel, t: TFunction) => {
  if (whitelabel?.name !== WHITELABEL_NAME.COVIDEO) {
    return <>{t('Inventory integration is not active.')}</>;
  }
  return (
    <Trans
      i18nKey='inventoryIntegrationNotActive'
      components={{
        wrapper: <div style={{ display: 'inline' }} />,
        br: <br />,
        phone: <a href='tel:8003061445'>800-306-1445</a>,
        email: <a href='mailto:support@covideo.com'>support@covideo.com</a>,
      }}
    />
  );
};

export const PREVIEW_COOKIE_NAME = 'covideo-preview-data';

export enum Sizes {
  HALF = '50%',
  THREE_QUARTER = '75%',
  FULL = '100%',
}

export const EMAIL_BUILDER_BORDER_RADIUS = '5px';

export enum Aligments {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right',
}

export const PREVIEWS = {
  desktop: 'desktop',
  mobile: 'mobile',
};

export const EMAIL_DESKTOP_WIDTH = 600;
export const EMAIL_MOBILE_WIDTH = 360;

/*
SUS-2504
Default thumbnail size should be same as code generated from PHP
PHP: max-width = 430px, max-height = 240px
Send & Share: max-width = 574 * 0.75 = 430px, max-height = 320 * 0.75 = 240px
*/
export const MAX_THUMBNAIL_HEIGHT = 320;
export const MAX_THUMBNAIL_WIDTH = 574;
export const MAX_THUMBNAIL_MOBILE_WIDTH = 325;

export const previewOptions = [
  {
    value: PREVIEWS.desktop,
    icon: <MdLaptop size={20} />,
  },
  {
    value: PREVIEWS.mobile,
    icon: <MdSmartphone size={20} />,
  },
];

type OptionsDictionary = {
  [key: string]: {
    label: string;
    value: string;
  };
};

export const getThumbnailOptionsDictionary = (
  t: TFunction
): OptionsDictionary => ({
  ANIMATED_SNAPSHOT: {
    label: t('Animated snapshot'),
    value: 'Animated Snapshot',
  },
  VIDEO_SNAPSHOT: {
    label: t('Video snapshot'),
    value: 'Video Snapshot',
  },
});

// Legacy export for backward compatibility
export const thumbnailOptionsDictionary: OptionsDictionary = {
  ANIMATED_SNAPSHOT: {
    label: 'Animated snapshot',
    value: 'Animated Snapshot',
  },
  VIDEO_SNAPSHOT: {
    label: 'Video snapshot',
    value: 'Video Snapshot',
  },
};

export const BUILDER_IDS = {
  EMPTY: '',
  VIDEO_THUMB: 'video-thumb',
  VIDEO_URL: 'video-url',
  SIGNATURE: 'signature',
};

export const CUSTOMER_FIRST_NAME_VARIABLE = '~FirstName~';
export const CUSTOMER_LAST_NAME_VARIABLE = '~LastName~';

export const getGeneralVariables = (t: TFunction) => [
  { value: '~ShortURL~', text: t('Video Share URL') },
];

export const getLegacyVariables = (t: TFunction) => [
  { value: '~FirstName~', text: t("Customer's first name") },
  { value: '~LastName~', text: t("Customer's last name") },
];

export const getServiceVariables = (t: TFunction) => [
  { value: CUSTOMER_FIRST_NAME_VARIABLE, text: t("Customer's first name") },
  { value: CUSTOMER_LAST_NAME_VARIABLE, text: t("Customer's last name") },
  { value: '~RepairOrderNumber~', text: t('Repair order number') },
  { value: '~QuoteTotal~', text: t('Total sum from a quote') },
  { value: '~TechnicianName~', text: t('Technician full name') },
  { value: '~AdvisorName~', text: t('Your name') },
  { value: '~AdvisorEmail~', text: t('Your email address') },
  { value: '~AdvisorPhone~', text: t('Your phone number') },
];

export const getSalesVariables = (t: TFunction) => [
  { value: CUSTOMER_FIRST_NAME_VARIABLE, text: t("Customer's first name") },
  { value: CUSTOMER_LAST_NAME_VARIABLE, text: t("Customer's last name") },
  { value: '~VinNumber~', text: t('VIN number') },
  { value: '~VehicleName~', text: t('Vehicle name') },
  { value: '~SalespersonName~', text: t('Salesperson full name') },
  { value: '~SalespersonEmail~', text: t('Salesperson email address') },
  { value: '~SalespersonPhone~', text: t('Salesperson phone number') },
];

// Legacy exports for backward compatibility
export const GENERAL_VARIABLES = [
  { value: '~ShortURL~', text: 'Video Share URL' },
];

export const LEGACY_VARIABLES = [
  { value: '~FirstName~', text: "Customer's first name" },
  { value: '~LastName~', text: "Customer's last name" },
];

export const SERVICE_VARIABLES = [
  { value: CUSTOMER_FIRST_NAME_VARIABLE, text: "Customer's first name" },
  { value: CUSTOMER_LAST_NAME_VARIABLE, text: "Customer's last name" },
  { value: '~RepairOrderNumber~', text: 'Repair order number' },
  { value: '~QuoteTotal~', text: 'Total sum from a quote' },
  { value: '~TechnicianName~', text: 'Technician full name' },
  { value: '~AdvisorName~', text: 'Your name' },
  { value: '~AdvisorEmail~', text: 'Your email address' },
  { value: '~AdvisorPhone~', text: 'Your phone number' },
];

export const SALES_VARIABLES = [
  { value: CUSTOMER_FIRST_NAME_VARIABLE, text: "Customer's first name" },
  { value: CUSTOMER_LAST_NAME_VARIABLE, text: "Customer's last name" },
  { value: '~VinNumber~', text: 'VIN number' },
  { value: '~VehicleName~', text: 'Vehicle name' },
  { value: '~SalespersonName~', text: 'Salesperson full name' },
  { value: '~SalespersonEmail~', text: 'Salesperson email address' },
  { value: '~SalespersonPhone~', text: 'Salesperson phone number' },
];

export const LIMIT_CONTACTS_PAGE_SIZE = 20;
export const LIMIT_GROUPS_PAGE_SIZE = 5;
export const CONTACTS_SORT_KEY = 'contact.firstName';
export const GROUPS_SORT_KEY = 'name';
export const SORT_ORDER = 'ASC';
export const BULK_IMPORT_DEFINITION = 'BULK';
